<script setup lang="ts">
// Drops the idle-session watcher into an authenticated shell and renders the
// warning it raises. Self-contained: mount it once near the root and nothing
// else has to know about session timing.
//
//   <SessionTimeoutGuard />
//
// The LeadGen SPA has its own copy of this shell (LgSessionGuard) because it
// runs on a separate design system — both drive the same composable.
import { computed } from 'vue'

import { useAuthStore } from '@/shared/stores/auth'
import { useSessionTimeout } from '@/shared/composables/useSessionTimeout'

const auth = useAuthStore()

const { warning, secondsLeft, stay, signOut } = useSessionTimeout({
    // Anonymous visitors have no session to lose.
    enabled: () => auth.isAuthenticated,
    onExpired: () => {
        auth.user = null
    },
})

const countdown = computed(() => {
    const mins = Math.floor(secondsLeft.value / 60)
    const secs = secondsLeft.value % 60
    return `${mins}:${String(secs).padStart(2, '0')}`
})
</script>

<template>
    <Teleport to="body">
        <div v-if="warning" class="st-backdrop" />
        <div
            v-if="warning"
            class="card st-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="st-title"
        >
            <h2 id="st-title" class="card-title">
                <FaIcon icon="triangle-exclamation" />
                Still there?
            </h2>
            <p class="st-body">
                You've been inactive for a while. For your security we'll sign
                you out in
                <strong class="st-count">{{ countdown }}</strong
                >.
            </p>
            <div class="st-actions">
                <button type="button" class="btn btn-secondary btn-sm" @click="signOut">
                    Sign out now
                </button>
                <button type="button" class="btn btn-gradient btn-sm" @click="stay">
                    Stay signed in
                </button>
            </div>
        </div>
    </Teleport>
</template>

<style scoped lang="scss">
.st-backdrop {
    position: fixed;
    inset: 0;
    z-index: var(--z-overlay);
    background: rgba(4, 3, 6, 0.7);
    backdrop-filter: blur(3px);
}

.st-dialog {
    position: fixed;
    z-index: var(--z-modal);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(28rem, calc(100vw - 2rem));
    gap: var(--small-margin);
    box-shadow: var(--shadow-lg);
    border-color: var(--color-warning);
}

.card-title {
    display: flex;
    align-items: center;
    gap: var(--small-margin);

    svg {
        color: var(--color-warning);
    }
}

.st-body {
    margin: 0;
    color: var(--color-secondary);
}

.st-count {
    font-family: var(--font-mono);
    color: var(--color-primary);
}

.st-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--small-margin);
    margin-top: var(--small-margin);
}
</style>
